import React, { useEffect, useMemo, useState } from 'react';
import { SearchModalStyles as S } from './SearchModal.styles';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import ChevronRight from '@img/icons/chevron_right.svg';
import SearchResultCard from '@stories/Components/Cards/SearchResultCard/SearchResultCard';
import { DownloadCardsStyles } from '@stories/Widgets/DownloadCards/DownloadCards.styles';
import Button from '@stories/Components/Buttons/GeneralButton/Button';
import { SystemIcon, getSvgFromSystem } from '@helpers/systemIcons';
import { AnimatePresence } from 'framer-motion';
import { withMotion } from '@hoc/withMotion';
import { useInView } from '@hooks/useInView';
import { MediaComponent } from '@helpers/mediaComponent';
import { SearchBar } from '../Header';
import HeaderStyles from '../Header.styles';
import EventResultCard from '@stories/Components/Cards/SearchResultCard/EventResultCard/EventResultCard';
import BgLeft from '@img/shapes/nav/bg-left.svg';
import BgRight from '@img/shapes/nav/bg-right.svg';
import BgSearch from '@img/shapes/nav/search.svg';
const SearchModal = ({ searchResponse, translations, search, viewAllEvents, viewAllNews, viewAllPages, nextPage, isSearching, performSearch, setSearch, setSearchResponse, popularSearches, }) => {
    const [elementRef, inView] = useInView({ threshold: 0.5 });
    const [tab, setTab] = useState('news');
    const memoisedSearch = useMemo(() => search, [searchResponse]);
    const activeTab = useMemo(() => {
        if (tab === 'events')
            return searchResponse?.events;
        if (tab === 'news')
            return searchResponse?.news;
        return searchResponse?.pages;
    }, [tab, searchResponse]);
    const viewAllLink = useMemo(() => {
        if (tab === 'events')
            return viewAllEvents;
        if (tab === 'news')
            return viewAllNews;
        return viewAllPages;
    }, [tab, viewAllEvents, viewAllNews, viewAllPages]);
    const viewAllText = useMemo(() => {
        if (tab === 'events')
            return translations['hyve.listing.view-all-events'];
        if (tab === 'news')
            return translations['hyve.listing.view-all-news'];
        return translations['hyve.listing.view-all-pages'];
    }, [tab, translations]);
    useEffect(() => {
        if (searchResponse) {
            if (tab === 'news' && searchResponse.news.totalResults === 0) {
                if (searchResponse.events.totalResults > 0) {
                    setTab('events');
                }
                else if (searchResponse.pages.totalResults > 0) {
                    setTab('pages');
                }
            }
            if (tab === 'events' && searchResponse.events.totalResults === 0) {
                if (searchResponse.events.totalResults > 0) {
                    setTab('events');
                }
                else if (searchResponse.pages.totalResults > 0) {
                    setTab('pages');
                }
            }
            if (tab === 'pages' && searchResponse.pages.totalResults === 0) {
                if (searchResponse.news.totalResults > 0) {
                    setTab('news');
                }
                else if (searchResponse.events.totalResults > 0) {
                    setTab('events');
                }
            }
        }
    }, [searchResponse, tab]);
    const tabText = useMemo(() => {
        if (tab === 'news') {
            return translations['hyve.global.news'];
        }
        if (tab === 'events') {
            return translations['hyve.global.events'];
        }
        if (tab === 'pages') {
            return translations['hyve.global.pages'];
        }
    }, [tab, translations]);
    const getResultsText = () => {
        return `
        ${translations['hyve.listing.showing']}
        <span>${activeTab?.items?.length} ${tabText} ${translations['hyve.listing.results']}</span>
        ${translations['hyve.listing.for']}
        <span>“${memoisedSearch}”</span>
    `;
    };
    const toTitleCase = (s) => s.substring(0, 1).toUpperCase() + s.substring(1);
    return (React.createElement(S.Container, { ref: elementRef, inView: inView },
        React.createElement(SectionWrapper, null,
            React.createElement(S.Background, null,
                React.createElement(BgLeft, null),
                React.createElement(BgSearch, null),
                React.createElement(BgRight, null)),
            React.createElement(MediaComponent, { mobile: true },
                React.createElement(SearchBar, { isSearching: isSearching, performSearch: performSearch, search: search, setSearch: setSearch, setSearchResponse: (searchRes) => setSearchResponse(searchRes) })),
            search === '' && popularSearches && (React.createElement(MediaComponent, { mobile: true },
                React.createElement(HeaderStyles.PopularSearches, null,
                    React.createElement("span", null,
                        translations['hyve.global.search.popular-searches'],
                        ":"),
                    React.createElement(HeaderStyles.PopularSearchList, null, popularSearches.map((popularSearch, i) => (React.createElement(HeaderStyles.PopularSearch, { href: popularSearch.url, key: `Header_PopularSearches_${i}` }, popularSearch.text))))))),
            React.createElement(AnimatePresence, null, memoisedSearch !== '' && activeTab?.items && searchResponse && (React.createElement(S.ResultsWrapper, { key: `SearchModal_Results` },
                React.createElement(S.TopBar, null,
                    React.createElement(S.ShowingResults, { dangerouslySetInnerHTML: { __html: getResultsText() } }),
                    React.createElement(S.Tabs, null,
                        React.createElement(S.Tab, { onClick: () => setTab('news'), "data-active": tab === 'news' },
                            toTitleCase(translations['hyve.global.news']),
                            " (",
                            searchResponse.news.totalResults,
                            ")"),
                        React.createElement(S.Tab, { onClick: () => setTab('events'), "data-active": tab === 'events' },
                            toTitleCase(translations['hyve.global.events']),
                            " (",
                            searchResponse.events.totalResults,
                            ")"),
                        React.createElement(S.Tab, { onClick: () => setTab('pages'), "data-active": tab === 'pages' },
                            toTitleCase(translations['hyve.global.pages']),
                            " (",
                            searchResponse.pages.totalResults,
                            ")")),
                    React.createElement(MediaComponent, { desktop: true },
                        React.createElement(S.ViewAll, { href: viewAllLink },
                            viewAllText,
                            React.createElement(ChevronRight, null)))),
                React.createElement(S.ResultsListing, null,
                    React.createElement(AnimatePresence, null, activeTab.items &&
                        activeTab.items.map((item, i) => tab === 'events' ? (React.createElement(EventResultCard, { key: `SearchModal_${tab}_${i}`, ...item })) : (React.createElement(SearchResultCard, { key: `SearchModal_${tab}_${i}`, ...item }))))),
                React.createElement(S.Pagination, null,
                    React.createElement(DownloadCardsStyles.ListPagination, null,
                        React.createElement(DownloadCardsStyles.ShowingWrapper, null,
                            translations['hyve.listing.showing'],
                            React.createElement("span", null, activeTab.items.length),
                            translations['hyve.listing.of'],
                            React.createElement("span", null, activeTab.totalResults),
                            translations['hyve.listing.results']),
                        React.createElement(DownloadCardsStyles.ProgressBar, { progress: (activeTab.items.length / activeTab.totalResults) * 100 }),
                        activeTab.items.length < activeTab.totalResults && (React.createElement(Button, { addon: getSvgFromSystem(SystemIcon.PlusThick), addonPosition: "left", branding: "black", styleType: "outline", title: translations['hyve.listing.load-more-results'] || 'Load more results', disabled: activeTab.items.length === activeTab.totalResults, onClick: () => nextPage(tab) }))))))))));
};
export default withMotion(SearchModal);
